import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  SegmentedControl,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconDeviceFloppy } from '@tabler/icons-react';
import { AssetBundleItem, usePutApiBotProviderAssetBundleSaveTextFile } from '@/API';
import { GetWebBotBundleUrl } from '@/APIURL';
import {
  ExtensionManifest,
  ExtensionManifestFormValues,
  ExtensionTemplateType,
  getDefaultManifestFormValues,
  manifestToFormValues,
  formValuesToManifest,
  SpineTemplateForm,
  ImageStatesTemplateForm,
  CustomHtmlTemplateForm,
  CommonSettingsForm,
  ExtensionPreview,
} from './ManifestEditor';

interface ExtensionManifestModalProps {
  opened: boolean;
  onClose: () => void;
  workspaceId: string;
  botProviderId: string;
  bundle: AssetBundleItem;
  onBundleUpdated?: (updatedBundle?: AssetBundleItem) => void;
}

export function ExtensionManifestModal({
  opened,
  onClose,
  workspaceId,
  botProviderId,
  bundle: initialBundle,
  onBundleUpdated,
}: ExtensionManifestModalProps) {
  const [currentBundle, setCurrentBundle] = useState<AssetBundleItem>(initialBundle);
  const [availableSpineAnimations, setAvailableSpineAnimations] = useState<string[]>([]);
  const saveTextFileMutation = usePutApiBotProviderAssetBundleSaveTextFile();

  useEffect(() => {
    setCurrentBundle(initialBundle);
  }, [initialBundle]);

  const handleBundleUpdated = (updated?: AssetBundleItem) => {
    if (updated) {
      setCurrentBundle(updated);
      onBundleUpdated?.(updated);
    }
  };

  const form = useForm<ExtensionManifestFormValues>({
    initialValues: getDefaultManifestFormValues(),
  });

  // Filter bundle files
  const allFiles = useMemo(() => currentBundle.files || [], [currentBundle.files]);
  const skeletonFiles = useMemo(() => allFiles.filter((f) => f.endsWith('.json') || f.endsWith('.skel')), [allFiles]);
  const atlasFiles = useMemo(() => allFiles.filter((f) => f.endsWith('.atlas')), [allFiles]);
  const imageFiles = useMemo(() => allFiles.filter((f) => /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(f)), [allFiles]);
  const htmlFiles = useMemo(() => allFiles.filter((f) => /\.(html|htm)$/i.test(f)), [allFiles]);

  // Try to load existing extension.json or apply heuristics
  useEffect(() => {
    if (!opened) return;

    fetch(GetWebBotBundleUrl(botProviderId, `${currentBundle.bundleName}/extension.json`))
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.json() as Promise<ExtensionManifest>;
      })
      .then((manifest) => {
        const loadedValues = manifestToFormValues(manifest);
        form.setValues(loadedValues);
        form.resetDirty(loadedValues);
      })
      .catch(() => {
        // Default heuristics if no extension.json yet
        let defaultTemplate: ExtensionTemplateType = 'image-states';
        let defaultSkeleton: string | null = null;
        let defaultAtlas: string | null = null;
        let defaultHtml: string | null = 'index.html';
        let defaultImage: string | null = null;

        if (skeletonFiles.length > 0 && atlasFiles.length > 0) {
          defaultTemplate = 'spine2d';
          defaultSkeleton = skeletonFiles[0];
          defaultAtlas = atlasFiles[0];
        } else if (htmlFiles.length > 0) {
          defaultTemplate = 'custom-html';
          defaultHtml = htmlFiles[0];
        } else if (imageFiles.length > 0) {
          defaultTemplate = 'image-states';
          defaultImage = imageFiles[0];
        }

        const fallbackValues = getDefaultManifestFormValues({
          templateType: defaultTemplate,
          skeletonFile: defaultSkeleton,
          atlasFile: defaultAtlas,
          entryHtml: defaultHtml,
          imageIdleConfig: defaultImage
            ? { strategy: 'on-complete', interval: 5, items: [{ animation: defaultImage, weight: 1, loop: true }] }
            : { strategy: 'on-complete', interval: 5, items: [] },
        });

        form.setValues(fallbackValues);
        form.resetDirty(fallbackValues);
      });
  }, [opened, currentBundle.bundleName, botProviderId]);

  // Extract animation names from skeleton JSON if available
  const skeletonFile = form.values.skeletonFile;
  useEffect(() => {
    if (!skeletonFile || !skeletonFile.endsWith('.json')) return;
    fetch(GetWebBotBundleUrl(botProviderId, `${currentBundle.bundleName}/${skeletonFile}`))
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.animations && typeof data.animations === 'object') {
          const names = Object.keys(data.animations);
          if (names.length > 0) {
            setAvailableSpineAnimations((prev) => Array.from(new Set([...prev, ...names])));
          }
        }
      })
      .catch(() => {});
  }, [skeletonFile, currentBundle.bundleName, botProviderId]);

  const handleSave = async () => {
    const manifestData = formValuesToManifest(form.values);

    try {
      await saveTextFileMutation.mutateAsync({
        params: {
          workspaceId,
          botProviderId,
          bundleName: currentBundle.bundleName,
          filePath: 'extension.json',
        },
        data: {
          content: JSON.stringify(manifestData, null, 2),
        },
      });

      notifications.show({
        title: '儲存成功',
        message: `資產包 ${currentBundle.bundleName} 的 extension.json 已更新`,
        color: 'teal',
        icon: <IconCheck size={16} />,
      });

      onClose();
    } catch (err: any) {
      notifications.show({
        title: '儲存失敗',
        message: err?.message || '儲存失敗，請稍後再試',
        color: 'red',
      });
    }
  };

  const templateType = form.values.templateType;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>設定資產包模板 ({currentBundle.bundleName})</Text>}
      size="lg"
      centered
    >
      <Stack gap="md">
        <div>
          <Text size="xs" c="dimmed" mb={4}>
            選擇渲染模板架構
          </Text>
          <SegmentedControl
            fullWidth
            value={templateType}
            onChange={(val) => form.setFieldValue('templateType', val as ExtensionTemplateType)}
            data={[
              { label: '多狀態圖片 / GIF', value: 'image-states' },
              { label: 'Spine 2D 動畫', value: 'spine2d' },
              { label: '自訂網頁 / HTML', value: 'custom-html' },
            ]}
          />
        </div>

        {templateType === 'spine2d' && (
          <SpineTemplateForm
            form={form}
            skeletonFiles={skeletonFiles}
            atlasFiles={atlasFiles}
            availableSpineAnimations={availableSpineAnimations}
            workspaceId={workspaceId}
            botProviderId={botProviderId}
            bundle={currentBundle}
            onBundleUpdated={handleBundleUpdated}
          />
        )}

        {templateType === 'image-states' && (
          <ImageStatesTemplateForm
            form={form}
            imageFiles={imageFiles}
            workspaceId={workspaceId}
            botProviderId={botProviderId}
            bundle={currentBundle}
            onBundleUpdated={handleBundleUpdated}
          />
        )}

        {templateType === 'custom-html' && (
          <CustomHtmlTemplateForm
            form={form}
            htmlFiles={htmlFiles}
            workspaceId={workspaceId}
            botProviderId={botProviderId}
            bundle={currentBundle}
            onBundleUpdated={handleBundleUpdated}
          />
        )}

        {/* Common Settings */}
        <CommonSettingsForm form={form} />

        {/* Live Simulator Preview */}
        <ExtensionPreview
          botProviderId={botProviderId}
          bundleName={currentBundle.bundleName || ''}
          templateType={templateType}
          formValues={form.values}
          onAnimationsLoaded={(anims) => {
            if (anims && anims.length > 0) {
              setAvailableSpineAnimations((prev) => Array.from(new Set([...prev, ...anims])));
            }
          }}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            取消
          </Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={saveTextFileMutation.isPending}
          >
            儲存 Manifest (extension.json)
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
